import { Button, CircularProgress, Typography, Box } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
// import { Recaptcha } from '../../../shared-components/recaptcha/Recaptcha'
import { CustomTextfield } from '../../../shared-components/textfield/CustomTextField'
import { ForgotPasswordConfirmationDialog } from '../../../shared-components/dialogs/forgot-password/ForgotPasswordConfirmationDialog'
import styles from './ForgotPassword.module.scss'
import instance from '../../../../providers/axios.config'
import { NavigationEnum } from '../../../../utils/constants'
import MailOutlineIcon from '@mui/icons-material/MailOutline'

export const ForgotPassword = () => {
  const { t } = useTranslation()
  const [isCaptchaOpen, setIsCaptchaOpen] = useState<boolean>(false)
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const forgotPassword = async (email: string) => {
    try {
      const response: any = await instance.post('auth/forgot-password', {
        email,
      })
      if (response.status === 'success') {
        // Show confirmation dialog on success
        setShowConfirmationModal(true)
      } else {
        setErrorMessage(t('Error') + ': ' + response.message)
      }
    } catch (error: any) {
      if (error.response && error.response.data) {
        setErrorMessage(error.response.data.message)
      } else {
        setErrorMessage(t('Error') + ': ' + error.message)
      }
    }
  }

  const handleForgotPassword = async () => {
    setErrorMessage('')

    // Validate email
    if (!email) {
      setErrorMessage(t('Email is required'))
      return
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrorMessage(t('Invalid email address'))
      return
    }

    setIsSubmitting(true)
    await forgotPassword(email)
    setIsSubmitting(false)
  }

  const handleConfirmationModalClose = () => {
    setShowConfirmationModal(false)
    // Optionally reset the form
    setEmail('')
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Typography className={styles.welcome}>
          {t('Forgot_password')}
        </Typography>

        {/* Description text */}
        <Typography className={styles.description}>
          {t('Enter your email address and we will send you a code to reset your password.')}
        </Typography>

        <CustomTextfield
          variant='outlined'
          label={t('Email')}
          className={styles.textField}
          value={email}
          error={!!errorMessage && errorMessage.toLowerCase().includes('email')}
          onChange={(e: any) => setEmail(e.target.value)}
          onKeyPress={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && !isSubmitting) {
              handleForgotPassword()
            }
          }}
        />

        {/* Disclaimer box */}
        <Box className={styles.disclaimerBox}>
          <MailOutlineIcon className={styles.disclaimerIcon} />
          <Typography className={styles.disclaimerText}>
            {t('After submitting, you will receive an email with a verification code. Use this code to reset your password.')}
          </Typography>
        </Box>

        {/* <Recaptcha isOpen={isCaptchaOpen} setIsOpen={setIsCaptchaOpen} /> */}

        {errorMessage && (
          <Typography className={styles.errorMessage}>
            {errorMessage}
          </Typography>
        )}

        <Button
          onClick={handleForgotPassword}
          className={styles.button}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : (
            t('Send reset code')
          )}
        </Button>

        <div className={styles.backToLogin}>
          <Typography>{t('Remember your password?')}</Typography>
          <Typography>
            <Link to={NavigationEnum.LOGIN} className={styles.linkText}>
              {t('Sign in')}
            </Link>
          </Typography>
        </div>
      </div>

      {/* Password Reset Confirmation Modal */}
      <ForgotPasswordConfirmationDialog
        open={showConfirmationModal}
        onClose={handleConfirmationModalClose}
        userEmail={email}
      />
    </div>
  )
}